import { uiText } from '@/i18n/interface';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrencySettings } from './useCurrencySettings';

interface PaymentMethod {
  id: string;
  name: string;
  label: string;
  method_type: string;
}

export const useDojoPaymentMethods = () => {
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { profile } = useAuth();
  const { defaultCurrency } = useCurrencySettings();

  useEffect(() => {
    fetchDojoPaymentMethods();
  }, [profile?.dojo_id]);

  // Listen for impersonation state changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'impersonationState') {
        fetchDojoPaymentMethods();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchDojoPaymentMethods = async () => {
    // Get effective dojo ID (handles impersonation)
    const impersonationState = sessionStorage.getItem('impersonationState')
    let effectiveDojoId = profile?.dojo_id

    if (impersonationState) {
      try {
        const parsed = JSON.parse(impersonationState)
        if (parsed.active && parsed.dojoId) {
          effectiveDojoId = parsed.dojoId
        }
      } catch (error) {
        console.warn('Failed to parse impersonation state:', error)
      }
    }

    if (!effectiveDojoId) {
      // Fallback to basic bank transfer only for new dojos
      setAvailablePaymentMethods([
        { id: 'bank_transfer', name: 'bank_transfer', label: uiText("Bank Transfer"), method_type: 'bank_transfer' }
      ]);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('id, method_type, method_name, is_active')
        .eq('dojo_id', effectiveDojoId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching payment methods:', error);
        // Fallback to basic bank transfer
        setAvailablePaymentMethods([
          { id: 'bank_transfer', name: 'bank_transfer', label: uiText("Bank Transfer"), method_type: 'bank_transfer' }
        ]);
        setIsLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        // No payment methods configured yet - show only bank transfer as default
        setAvailablePaymentMethods([
          { id: 'bank_transfer', name: 'bank_transfer', label: uiText("Bank Transfer"), method_type: 'bank_transfer' }
        ]);
      } else {
        // Map actual configured payment methods
        const methods = data.map(method => ({
          id: method.method_type,
          name: method.method_type,
          label: method.method_name,
          method_type: method.method_type
        }));
        setAvailablePaymentMethods(methods);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      // Fallback to basic bank transfer
      setAvailablePaymentMethods([
        { id: 'bank_transfer', name: 'bank_transfer', label: uiText("Bank Transfer"), method_type: 'bank_transfer' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    availablePaymentMethods,
    defaultCurrency,
    isLoading,
    refreshPaymentMethods: fetchDojoPaymentMethods
  };
};